import { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, CalendarDays, CheckCircle2, FolderKanban, Users } from 'lucide-react';
import {
  apiGetDashboard,
  mapDashboardSummary,
} from '../api/client';
import { useWorkspaceAdds } from '../workspaceAddsContext';

/** Solid bar fills for project progress (primary + distinct hues, no gradients). */
const PROJECT_BAR_COLORS = ['bg-brand-500', 'bg-green-500', 'bg-amber-500', 'bg-pink-500', 'bg-brand-400', 'bg-green-400'];

function formatDashboardDueDateLocalized(iso, t) {
  if (iso == null || iso === '') return '—';
  const d = new Date(String(iso));
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const dayStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((dayStart(d) - dayStart(now)) / 86400000);
  const time = `${d.getHours() % 12 || 12}:${d.getMinutes().toString().padStart(2, '0')} ${
    d.getHours() < 12 ? 'AM' : 'PM'
  }`;
  if (diff === 0) return `${t('today')} · ${time}`;
  if (diff === 1) return `${t('tomorrow')} · ${time}`;
  if (diff === -1) return `${t('yesterday')} · ${time}`;
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} · ${time}`;
}

function dashboardPriorityLabel(priority, t) {
  const labels = {
    Low: 'priorityLow',
    Medium: 'priorityMedium',
    High: 'priorityHigh',
  };
  return t(labels[priority] || priority);
}

function DashboardPage({ t, onNavigate, onToast }) {
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const workspaceAddsAllowed = useWorkspaceAdds();

  const refresh = useCallback(async () => {
    try {
      const raw = await apiGetDashboard();
      setDash(mapDashboardSummary(raw));
    } catch (err) {
      onToast?.(err instanceof Error ? err.message : String(err));
      setDash(null);
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const topStats = useMemo(() => {
    const d = dash;
    const safe = {
      totalProjects: d?.totalProjects ?? 0,
      openTasks: d?.openTasks ?? 0,
      completedTasks: d?.completedTasks ?? 0,
      teamMemberCount: d?.teamMemberCount ?? 0,
      teamMembersOnline: d?.teamMembersOnline ?? 0,
      projectsAddedThisMonth: d?.projectsAddedThisMonth ?? 0,
      tasksDueSoonCount: d?.tasksDueSoonCount ?? 0,
      tasksCompletedToday: d?.tasksCompletedToday ?? 0,
    };
    return [
      {
        title: t('dashboardStatTotalProjects'),
        value: String(safe.totalProjects),
        delta:
          safe.projectsAddedThisMonth > 0
            ? `+${safe.projectsAddedThisMonth} ${t('dashboardThisMonth')}`
            : t('dashboardNoNewProjectsThisMonth'),
        icon: FolderKanban,
        iconBg: 'bg-brand-100 text-brand-600',
      },
      {
        title: t('dashboardStatOpenTasks'),
        value: String(safe.openTasks),
        delta:
          safe.tasksDueSoonCount > 0
            ? `${safe.tasksDueSoonCount} ${t('dashboardDueNext7Days')}`
            : t('dashboardNothingDueThisWeek'),
        icon: Activity,
        iconBg: 'bg-amber-100 text-amber-600',
      },
      {
        title: t('dashboardStatCompletedTasks'),
        value: String(safe.completedTasks),
        delta:
          safe.tasksCompletedToday > 0
            ? `+${safe.tasksCompletedToday} ${t('dashboardTodayUtc')}`
            : t('dashboardNoCompletionsToday'),
        icon: CheckCircle2,
        iconBg: 'bg-green-100 text-green-600',
      },
      {
        title: t('dashboardStatTeamMembers'),
        value: String(safe.teamMemberCount),
        delta:
          safe.teamMembersOnline > 0
            ? `${safe.teamMembersOnline} ${t('dashboardOnline')}`
            : safe.teamMemberCount ? t('dashboardNoneMarkedOnline') : t('dashboardInviteFromTeam'),
        icon: Users,
        iconBg: 'bg-pink-100 text-pink-600',
      },
    ];
  }, [dash, t]);

  const projectProgress = useMemo(() => {
    if (!dash?.projects?.length) return [];
    return dash.projects.map((p, idx) => ({
      key: `p-${p.projectId}`,
      name: p.name,
      projectNumber: p.projectNumber || '',
      progress: p.progressPercent,
      color: PROJECT_BAR_COLORS[idx % PROJECT_BAR_COLORS.length],
    }));
  }, [dash]);

  const weeklyMax = useMemo(() => {
    const w = dash?.weeklyThroughput ?? [];
    return Math.max(1, ...w);
  }, [dash]);

  return (
    <section className="space-y-4">
      <div className="rounded-2xl border-0 bg-[#1A1A1A] p-5 text-white shadow-lg" style={{background:'linear-gradient(135deg,#1A1A1A 0%,#5BB5D5 60%,#4CB87E 100%)'}}>
        <h2 className="text-2xl font-semibold">{t('dashboardHeroTitle')}</h2>
        <p className="mt-1 text-sm text-white/90">{t('dashboardHeroSubtitle')}</p>
        {workspaceAddsAllowed && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onNavigate?.('projects')}
              className="rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium backdrop-blur hover:bg-white/30"
            >
              {t('dashboardActionNewTask')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.('projects')}
              className="rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium backdrop-blur hover:bg-white/30"
            >
              {t('dashboardActionCreateProject')}
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.('team')}
              className="rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium backdrop-blur hover:bg-white/30"
            >
              {t('dashboardActionInviteTeam')}
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">{t('dashboardLoading')}</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {topStats.map((stat) => (
              <article
                key={stat.title}
                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900"
              >
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{stat.title}</p>
                  <div className={`rounded-xl p-2 ${stat.iconBg}`}>
                    <stat.icon size={15} />
                  </div>
                </div>
                <p className="mt-2 text-2xl font-semibold text-slate-800 dark:text-white">{stat.value}</p>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">{stat.delta}</p>
              </article>
            ))}
          </div>

          <div className="grid gap-4 lg:grid-cols-12">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:col-span-7">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('dashboardProjectProgress')}</h3>
                <button
                  type="button"
                  onClick={() => onNavigate?.('projects')}
                  className="rounded-md border border-brand-200 px-2 py-1 text-[11px] dark:border-brand-900"
                >
                  {t('viewAll')}
                </button>
              </div>
              {projectProgress.length === 0 ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('dashboardNoProjectsYetCreate')}
                </p>
              ) : (
                <div className="space-y-3">
                  {projectProgress.map((item) => (
                    <div key={item.key}>
                      <div className="mb-1 flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                        <span className="truncate pr-2">
                          {item.projectNumber ? (
                            <>
                              <span className="font-mono font-semibold text-brand-700 dark:text-brand-400">
                                {item.projectNumber}
                              </span>
                              <span className="mx-1 text-slate-400 dark:text-slate-500">·</span>
                            </>
                          ) : null}
                          {item.name}
                        </span>
                        <span>{item.progress}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                        <div
                          className={`h-2 rounded-full ${item.color} transition-all duration-500`}
                          style={{ width: `${item.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:col-span-5">
              <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">{t('dashboardUpcomingTasks')}</h3>
              {!dash?.upcomingTasks?.length ? (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('dashboardNoDatedOpenTasks')}
                </p>
              ) : (
                <div className="space-y-2">
                  {dash.upcomingTasks.map((task) => (
                    <button
                      key={`${task.taskId}-${task.projectId}`}
                      type="button"
                      onClick={() => onNavigate?.('projects')}
                      className="w-full rounded-lg border border-brand-100/80 p-2.5 text-left text-xs hover:bg-brand-50 dark:border-brand-900/40 dark:hover:bg-brand-900/15"
                    >
                      <div className="flex flex-wrap items-baseline gap-2 gap-y-0">
                        {task.taskNumber ? (
                          <span className="font-mono text-[11px] font-semibold text-brand-600 dark:text-brand-400">
                            {task.taskNumber}
                          </span>
                        ) : null}
                        <p className="min-w-0 flex-1 font-medium text-slate-700 dark:text-slate-200">{task.title}</p>
                      </div>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {task.projectNumber ? `${task.projectNumber} · ` : ''}
                        {task.projectName}
                      </p>
                      <div className="mt-1 flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-300">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays size={11} />
                          {formatDashboardDueDateLocalized(task.dueDateUtc, t)}
                        </span>
                        <span>{dashboardPriorityLabel(task.priority, t)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-12">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:col-span-8">
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('dashboardWeeklyThroughput')}</h3>
                {dash?.weeklyThroughputBasedOnCreations && (
                  <span className="text-[10px] text-slate-500 dark:text-slate-400">{t('dashboardTasksCreatedNoCompletionsYet')}</span>
                )}
              </div>
              <div className="flex h-32 items-end gap-2 rounded-xl border border-brand-100/80 p-3 dark:border-brand-900/40">
                {(dash?.weeklyThroughput ?? [0, 0, 0, 0, 0, 0, 0]).map((value, idx) => (
                  <div key={`bar-${idx}`} className="flex-1" title={`${t('dashboardChartDay')} ${idx + 1}: ${value}`}>
                    <div
                      className="rounded-t bg-brand-500"
                      style={{ height: `${Math.max(8, (value / weeklyMax) * 104)}px` }}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 lg:col-span-4">
              <h3 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">{t('dashboardRecentActivity')}</h3>
              <div className="space-y-2">
                {(dash?.activityLines ?? []).map((entry) => (
                  <div
                    key={entry}
                    className="rounded-lg border border-brand-100/80 p-2 text-xs text-slate-600 dark:border-brand-900/40 dark:text-slate-300"
                  >
                    {entry}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

export default DashboardPage;
